import { isNil, trim } from 'lodash';
import { FieldValues, Path, PathValue, UseFormSetValue } from 'react-hook-form';

const extractComponent = (
  components: google.maps.places.AddressComponent[],
  type: string,
) => components.find(component => component.types.includes(type));

export const extractAddressComponents = (
  place: Partial<google.maps.places.Place>,
) => {
  const components = place.addressComponents || [];

  const streetName = extractComponent(components, 'route')?.longText ?? '';
  const streetNumber =
    extractComponent(components, 'street_number')?.longText ?? '';
  const neighborhood =
    extractComponent(components, 'neighborhood')?.longText ?? '';
  const city = extractComponent(components, 'locality')?.longText ?? '';
  const county =
    extractComponent(components, 'administrative_area_level_2')?.longText ?? '';
  const state = trim(
    extractComponent(components, 'administrative_area_level_1')?.shortText ??
      '',
  );
  const country = extractComponent(components, 'country')?.longText ?? '';
  const zipCode = extractComponent(components, 'postal_code')?.longText ?? '';

  return {
    streetName,
    streetNumber,
    neighborhood,
    county,
    city,
    state,
    country,
    zipCode,
  };
};

interface AddressFields extends FieldValues {
  google_places_id: string | null;
  street: string | null;
  suite: string | null;
  city: string | null;
  county: string | null;
  country: string | null;
  zip_code: string | null;
  state: string | null;
}

interface IFillAddressFormValues<T extends Partial<AddressFields>> {
  setValue: UseFormSetValue<T>;
  place: Partial<google.maps.places.Place>;
}

const setFormValue = <T extends FieldValues>(
  setValue: UseFormSetValue<T>,
  field: Path<T>,
  value: PathValue<T, Path<T>>,
) => {
  setValue(field, value, { shouldValidate: true });
};

//This function aims to automatically populate form address fields when a Google
//Places location is selected.
//Make sure to name the address fields in your form according to this interface.
export const fillAddressFormValues = <T extends Partial<AddressFields>>({
  setValue,
  place,
}: IFillAddressFormValues<T>) => {
  const { streetName, streetNumber, county, city, state, zipCode, country } =
    extractAddressComponents(place);

  if (!isNil(place.id)) {
    setFormValue(
      setValue,
      'google_places_id' as Path<T>,
      place.id as PathValue<T, Path<T>>,
    );

    const fullStreet = `${streetNumber ? streetNumber + ' ' : ''}${streetName}`;
    setFormValue(
      setValue,
      'street' as Path<T>,
      fullStreet as PathValue<T, Path<T>>,
    );

    setFormValue(setValue, 'city' as Path<T>, city as PathValue<T, Path<T>>);
    setFormValue(
      setValue,
      'county' as Path<T>,
      county as PathValue<T, Path<T>>,
    );

    setFormValue(
      setValue,
      'country' as Path<T>,
      country as PathValue<T, Path<T>>,
    );

    setFormValue(setValue, 'state' as Path<T>, state as PathValue<T, Path<T>>);

    setFormValue(
      setValue,
      'zip_code' as Path<T>,
      zipCode as PathValue<T, Path<T>>,
    );
  }
};
